import {useMemo} from 'react';
import {FORM_TYPE_ILLEGAL} from '@/constants/help/index';
import {useFormContainerContext} from '@/components/help/common/FormContainer/FormContainerContext';

interface UseCustomPlaceholderProps {
  value: string;
  template?: string;
  placeholder?: string;
}

interface UseCustomPlaceholderReturn {
  shouldShowCustomPlaceholder: boolean;
  actualPlaceholder: string;
}

/**
 * TextArea의 커스텀 플레이스홀더 표시 로직을 관리하는 훅
 * - 불법촬영물 신고 폼에서만 커스텀 플레이스홀더 표시
 * - 값이 비어있거나 템플릿과 동일할 때만 표시
 */
export const useCustomPlaceholder = ({
  value,
  template,
  placeholder,
}: UseCustomPlaceholderProps): UseCustomPlaceholderReturn => {
  const {type} = useFormContainerContext();
  const isIllegalFormType = useMemo(() => type === FORM_TYPE_ILLEGAL, [type]);

  const isValueEmptyOrTemplate = useMemo(() => {
    const trimmedValue = value.trim();
    return !trimmedValue || trimmedValue === template?.trim();
  }, [value, template]);

  const shouldShowCustomPlaceholder = useMemo(() => {
    return (
      isIllegalFormType &&
      Boolean(template) &&
      Boolean(placeholder) &&
      isValueEmptyOrTemplate
    );
  }, [isIllegalFormType, template, placeholder, isValueEmptyOrTemplate]);

  const actualPlaceholder = useMemo(() => {
    // 커스텀 플레이스홀더를 보여줘야 하는 경우 빈 문자열
    if (shouldShowCustomPlaceholder) {
      return '';
    }

    // 템플릿이 있고 사용자 입력이 비어있는 경우 템플릿을 placeholder로 사용
    if (template && value.trim() === '') {
      return template;
    }

    // 기본 placeholder 사용
    return placeholder ?? '';
  }, [shouldShowCustomPlaceholder, template, value, placeholder]);

  return {
    shouldShowCustomPlaceholder,
    actualPlaceholder,
  };
};
